import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./CustomFieldFilterBuilder.module.css";
import {
  CustomFieldSearchOperator,
  CustomFieldValuesSearchPredicate,
  CustomFieldValuesSearchPredicateConst,
  StringIndexedDict,
} from "../../../api/GenericTypes";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { CustomFieldsVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/CustomFieldsVirtualizedSelectForm";
import {
  Control,
  FieldErrors,
  Path,
  useForm,
  UseFormGetValues,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { GenericVirtualizedSelectFormField } from "../../../common/formfields/GenericVirtualizedSelectFormField/GenericVirtualizedSelectFormField";
import { CustomField } from "../../../api/CustomFields/CustomFields";
import { CustomFieldDataTypeUtils } from "../../../api/CustomFields/CustomFieldsBase";
import { CustomFieldFilterFormRenderer } from "../../CustomFields/CustomFieldEditRenderer";
import {
  convertCustomFieldSearchQueryForm,
  convertICustomFieldSearch,
  countNodesInCustomFieldSearchQuery,
  CustomFieldFilterType,
  yupCustomFieldFilterValidationResolver,
} from "./CustomFieldFilterUtils";
import { useResizeDetector } from "react-resize-detector";
import { getError } from "../../../common/formfields/GenericVirtualizedSelectFormField/GenericVirtualizedEntitySelectFormFieldControllerWrapper";
import { FormFieldError } from "../../../common/formfields/FormFieldLayouts";
import { InputFormField } from "../../../common/formfields/InputFormField";
import { OverlayInfo } from "../../../common/misc/OverlayInfo/OverlayInfo";
import { Modal } from "../../../ELN/common/ELNModal/ELNModal";
import { v4 as uuidv4 } from "uuid";
import { RenderCustomFieldOperator } from "./CustomFieldFilterRenderUtils";
import { FormErrors } from "../../../common/forms/FormButtons";
import { MultiEditMappedItemObject } from "../../../common/forms/MultiEditForms/common/MultiEditProvider";
import { NotAvailable } from "../../../common/misc/UIconstants";
import { CustomTypeFilters } from "../../../api/CustomTypes/CustomTypes";

const gridWidth = 10 as const;
export interface CustomFieldSearchQueryForm {
  predicate?: CustomFieldValuesSearchPredicate | null;
  left?: CustomFieldSearchQueryForm | null;
  right?: CustomFieldSearchQueryForm | null;
  customFieldId?: number | null;
  operator?: CustomFieldSearchOperator | null;
  value?: any | null;
  // These are not part of the CustomFieldSearchQueryForm interface, but added for convenience
  customField?: CustomField | null; // Purely for frontend rendering purposes
  dataType?: CustomField["dataType"] | null; // Purely for frontend rendering purposes
}

/**
 * Inserts an element into an object at the specified path.
 * @author @CorradoSurmanowicz
 * @template T - A type that extends StringIndexedDict.
 * @param {T} object - The object into which the element will be inserted.
 * @param {string} path - The dot-separated path where the element should be inserted.
 * @param {any} elem - The element to insert at the specified path.
 * @returns {void}
 *
 * @example
 * const obj = { a: { b: { c: 1 } } };
 * insertByPath(obj, 'a.b.d', 2);
 * // obj is now { a: { b: { c: 1, d: 2 } } }
 */
const insertByPath = <T extends StringIndexedDict>(object: T, path: string, elem: any) => {
  let currentObject = object;
  const parts = path.split(".");
  const last = parts.pop();
  for (const part of parts) {
    currentObject = currentObject[part];
    if (!currentObject) {
      return;
    }
  }
  if (last) {
    (currentObject as any)[last] = elem;
    // delete currentObject[last];
  }
};

interface HeightRef {
  [key: string]: number;
}
interface RenderTreeStructureChildProps {
  height: HeightRef;
  setHeight: React.Dispatch<React.SetStateAction<HeightRef>>;
}
interface RenderTreeStructurStateProviderProps {
  children: ({ height, setHeight }: RenderTreeStructureChildProps) => React.ReactNode;
}
const RenderTreeStructurStateProvider = ({ children }: RenderTreeStructurStateProviderProps) => {
  const [height, setHeight] = useState<HeightRef>({});
  return <>{children({ height, setHeight })}</>;
};

/**
 * Props for rendering the filter structure.
 *
 * @interface RenderFilterStructureProps
 * @author @CorradoSurmanowicz
 * @property {boolean} [isFirstNode] - Indicates if this is the first node in the structure.
 * @property {boolean} [isLastNode] - Indicates if this is the last node in the structure.
 * @property {string} [id] - The unique identifier for the filter node.
 * @property {Control<ICustomFieldSearchForm, any, ICustomFieldSearchForm>} control - The control object from react-hook-form.
 * @property {UseFormRegister<ICustomFieldSearchForm>} register - The register function from react-hook-form.
 * @property {FieldErrors<ICustomFieldSearchForm>} errors - The errors object from react-hook-form.
 * @property {UseFormReset<ICustomFieldSearchForm>} reset - The reset function from react-hook-form.
 * @property {UseFormSetValue<ICustomFieldSearchForm>} setValue - The setValue function from react-hook-form.
 * @property {UseFormGetValues<ICustomFieldSearchForm>} getValues - The getValues function from react-hook-form.
 * @property {(value: any, onChange: (...event: any[]) => void) => Promise<void>} onChangeOverride - A function to override the default onChange behavior.
 * @property {React.MutableRefObject<{ [key: string]: number }>} heightRef - A mutable ref object to store the height of each node.
 */
interface RenderFilterStructureProps {
  id?: string;
  control: Control<ICustomFieldSearchForm, any, ICustomFieldSearchForm>;
  register: UseFormRegister<ICustomFieldSearchForm>;
  errors: FieldErrors<ICustomFieldSearchForm>;
  setValue: UseFormSetValue<ICustomFieldSearchForm>;
  customFieldsDict: MultiEditMappedItemObject<CustomField>;
  entityTypes: CustomTypeFilters["entityTypes"];
}

interface RenderFilterTreeStructureProps extends RenderFilterStructureProps, RenderTreeStructureChildProps {
  reset: UseFormReset<ICustomFieldSearchForm>;
  getValues: UseFormGetValues<ICustomFieldSearchForm>;
}
// Render filter tree structure
const RenderFilterTreeStructure = ({
  id = "",
  control,
  register,
  errors,
  reset,
  setValue,
  getValues,
  height,
  setHeight,
  customFieldsDict,
  entityTypes,
}: RenderFilterTreeStructureProps) => {
  const filters = useWatch({ control, name: "filterQuery" }); // Entire filter structure
  const filter = useWatch({ control, name: id as any }) as CustomFieldSearchQueryForm | null; // Current filter node
  const { ref, height: refHeight } = useResizeDetector({
    handleHeight: true,
    handleWidth: false,
    onResize: (height) => {
      if (!!height) setHeight((prev) => ({ ...prev, [id]: height }));
    },
  });
  const [hoverPredicate, setHoverPredicate] = useState(false);
  // const [hoverDeletePredicateBtn, setHoverDeletePredicateBtn] = React.useState(false);
  const [hoverFilter, setHoverFilter] = useState(false);
  // console.log(`ID: ${id}`, filter);

  const onRowStartRenderer = useCallback((item: CustomFieldSearchOperator) => {
    return <RenderCustomFieldOperator operator={item} style={{ width: 20, height: 20, flexShrink: 0 }} />;
  }, []);

  useEffect(() => {
    if (!!refHeight) {
      setHeight((prev) => ({ ...prev, [id]: refHeight }));
    }
  }, [id, refHeight, setHeight]);

  if (!filter) {
    const _error = getError(`${id}` as any, errors);
    return (
      <div
        className={`connector ${id} ${styles.filterElementContainer} ${_error ? "border-danger react-select" : ""}`}
        id={id}
        ref={ref}
      >
        <div className="flex row-nowrap align-center gap-5">
          <button
            className="btn btn-block btn-ghost-primary"
            onClick={() => {
              const _filters = structuredClone(filters);
              const _id = id.replace("filterQuery.", "");
              if (_id === "filterQuery") {
                reset({ filterQuery: { customField: null, operator: null, value: "" } });
              } else {
                if (_filters) {
                  insertByPath(_filters, _id, { customField: null, operator: "", value: "" });
                  reset({ filterQuery: _filters });
                }
              }
            }}
          >
            <LucideIcon name="filter" /> Configure filter
          </button>
          <span style={{ color: "var(--gray-400)" }}> or </span>
          <button
            className="btn btn-block btn-ghost-primary"
            onClick={() => {
              const _filters = structuredClone(filters);
              const _id = id.replace("filterQuery.", "");
              if (_id === "filterQuery") {
                reset({ filterQuery: { left: null, right: null, predicate: "AND" } });
              } else {
                if (_filters) {
                  insertByPath(_filters, _id, { left: null, right: null, predicate: "AND" });
                  reset({ filterQuery: _filters });
                }
              }
            }}
          >
            <LucideIcon name="network" /> Add node
          </button>
        </div>
        <FormFieldError id={`${id}` as any} errors={errors} />
      </div>
    );
  }

  // Render Filter Element
  if (!filter.predicate) {
    return (
      <div
        className={`connector ${id} ${styles.filterElementContainer}`}
        id={id}
        ref={ref}
        onMouseEnter={() => setHoverFilter(true)}
        onMouseLeave={() => setHoverFilter(false)}
      >
        <div className={styles.filterElement} style={{ position: "relative" }}>
          <div className={styles.filterElementForm}>
            <div>
              <input type="text" {...register(`${id}.dataType` as any)} readOnly hidden />
              <CustomFieldsVirtualizedSelectForm
                id={`${id}.customField` as any}
                filters={{ ids: Object.keys(customFieldsDict).map((e) => +e) }}
                control={control}
                horizontal={false}
                required
                entityTypeConstraint={entityTypes}
                onChangeOverride={(value, onChange) => {
                  if (value && typeof value === "object" && Object.hasOwn(value, "id")) {
                    setValue(`${id}.dataType` as Path<ICustomFieldSearchForm>, customFieldsDict[value.id].dataType);
                    setValue(`${id}.customFieldId` as Path<ICustomFieldSearchForm>, value.id);
                    setValue(`${id}.customField` as Path<ICustomFieldSearchForm>, {
                      ...customFieldsDict[value.id],
                      id: customFieldsDict[value.id].id,
                      name: customFieldsDict[value.id].name,
                      dataType: customFieldsDict[value.id].dataType,
                    });
                    setValue(`${id}.value` as Path<ICustomFieldSearchForm>, null);
                    if (
                      !CustomFieldDataTypeUtils.IsValidOperatorForCustomFieldDataType(
                        filter.operator,
                        customFieldsDict[value.id].dataType
                      )
                    ) {
                      setValue(`${id}.operator` as Path<ICustomFieldSearchForm>, "EQUALS");
                    }
                  } else {
                    setValue(`${id}.dataType` as Path<ICustomFieldSearchForm>, null);
                    setValue(`${id}.operator` as Path<ICustomFieldSearchForm>, "EQUALS");
                    setValue(`${id}.customFieldId` as Path<ICustomFieldSearchForm>, null);
                    onChange(value);
                  }
                }}
              />
            </div>
            <div>
              <GenericVirtualizedSelectFormField
                id={`${id}.operator` as any}
                label={"Operator"}
                items={
                  filter?.customField?.id
                    ? Array.from(
                        CustomFieldDataTypeUtils.MapCustomFieldValueOperatorToCustomFieldDataType(
                          customFieldsDict?.[filter.customField.id].dataType
                        )
                      )
                    : []
                }
                control={control}
                placeholder={"Select operator"}
                required={true}
                isMulti={false}
                allowCreateEntity={false}
                horizontal={false}
                allowX={false}
                onRowStartRenderer={onRowStartRenderer}
              />
            </div>
            <div>
              {filter.customField && (
                <CustomFieldFilterFormRenderer
                  control={control}
                  errors={errors}
                  id={`${id}.value` as any}
                  operator={filter?.operator}
                  customField={filter.customField}
                />
              )}
            </div>
          </div>
          {hoverFilter && (
            <div
              className={styles.predicateDelete}
              onMouseEnter={() => {
                document.querySelectorAll(`[class*="connector ${id}"]`).forEach((element) => {
                  element.classList.add(styles.connectorLineDelete);
                });
              }}
              onMouseLeave={() => {
                document.querySelectorAll(`[class*="connector ${id}"]`).forEach((element) => {
                  element.classList.remove(styles.connectorLineDelete);
                });
              }}
              onClick={() => {
                const _filters = structuredClone(filters);
                const _id = id.replace("filterQuery.", "");
                if (_id === "filterQuery") {
                  reset({ filterQuery: null });
                } else {
                  if (_filters) {
                    insertByPath(_filters, _id, null);
                    reset({ filterQuery: _filters });
                  }
                }
              }}
              title={"Delete node"}
            >
              <LucideIcon name="trash-2" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.filterElements} ref={ref} id={id}>
      <div
        className={styles.predicate}
        onMouseEnter={() => setHoverPredicate(true)}
        onMouseLeave={() => setHoverPredicate(false)}
      >
        {id && (
          <GenericVirtualizedSelectFormField
            id={`${id}.predicate`}
            label={"Predicate"}
            items={Array.from(CustomFieldValuesSearchPredicateConst)}
            control={control}
            placeholder={"Select operator"}
            required={true}
            isMulti={false}
            allowCreateEntity={false}
            horizontal={false}
            allowX={false}
            uncontained
            style={{ maxHeight: "min-content", minHeight: "min-content", height: "min-content" }}
          >
            {hoverPredicate && (
              <div
                className={styles.predicateDelete}
                onMouseEnter={() => {
                  document.querySelectorAll(`[class*="connector ${id}"]`).forEach((element) => {
                    element.classList.add(styles.connectorLineDelete);
                  });
                }}
                onMouseLeave={() => {
                  document.querySelectorAll(`[class*="connector ${id}"]`).forEach((element) => {
                    element.classList.remove(styles.connectorLineDelete);
                  });
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const _filters = structuredClone(filters);
                  const _id = id.replace("filterQuery.", "");
                  if (_id === "filterQuery") {
                    reset({ filterQuery: null });
                  } else {
                    if (_filters) {
                      insertByPath(_filters, _id, null);
                      reset({ filterQuery: _filters });
                    }
                  }
                }}
                title={"Delete corresponding nodes"}
              >
                <LucideIcon name="trash-2" />
              </div>
            )}
          </GenericVirtualizedSelectFormField>
        )}
      </div>
      <div
        className={`connector ${id} ${styles.connectorLine}`}
        style={{ borderTop: `1px solid transparent`, height: 1, width: gridWidth }}
      />
      <div style={{ height: "100%", width: gridWidth, position: "relative" }}>
        <div
          className={`connector ${id} ${styles.connectorLine}`}
          style={{
            borderLeft: `1px solid transparent`,
            borderTop: `1px solid transparent`,
            borderBottom: `1px solid transparent`,
            width: gridWidth,
            height: (height[id.concat(`.left`)] ?? 0) / 2 + (height[id.concat(`.right`)] ?? 0) / 2,
            top: (height[id.concat(`.left`)] ?? 0) / 2,
            bottom: (height[id.concat(`.right`)] ?? 0) / 2,
            position: "absolute",
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexFlow: "column nowrap",
          width: "100%",
          height: "fit-content",
          overflow: "visible",
        }}
      >
        {/* Render left side */}
        <RenderFilterTreeStructure
          id={id.concat(`.left`)}
          control={control}
          register={register}
          errors={errors}
          reset={reset}
          setValue={setValue}
          getValues={getValues}
          height={height}
          setHeight={setHeight}
          customFieldsDict={customFieldsDict}
          entityTypes={entityTypes}
        />
        {/* Render right side */}
        <RenderFilterTreeStructure
          id={id.concat(`.right`)}
          control={control}
          register={register}
          errors={errors}
          reset={reset}
          setValue={setValue}
          getValues={getValues}
          height={height}
          setHeight={setHeight}
          customFieldsDict={customFieldsDict}
          entityTypes={entityTypes}
        />
      </div>
    </div>
  );
};

const RenderSimpleFilterStructure = ({
  // filter,
  id = "",
  control,
  register,
  errors,
  setValue,
  customFieldsDict,
  entityTypes,
}: RenderFilterStructureProps) => {
  // const filters = useWatch({ control, name: "filterQuery" }); // Entire filter structure
  const filter = useWatch({ control, name: id as any }) as CustomFieldSearchQueryForm | null; // Current filter node
  const onRowStartRenderer = useCallback((item: CustomFieldSearchOperator) => {
    return <RenderCustomFieldOperator operator={item} style={{ width: 20, height: 20, flexShrink: 0 }} />;
  }, []);
  // const customField = useWatch({ control, name: `${id}.customField` as any }); // Entire filter structure
  // console.log("Custom field watch", customField);

  if (!filter) return <>{NotAvailable}</>;
  return (
    <div className={styles.filterElementForm}>
      <div>
        <input type="text" {...register(`${id}.dataType` as any)} readOnly hidden />
        <CustomFieldsVirtualizedSelectForm
          id={`${id}.customField` as any}
          filters={{ ids: Object.keys(customFieldsDict).map((e) => +e) }}
          control={control}
          horizontal={false}
          required
          entityTypeConstraint={entityTypes}
          onChangeOverride={(value, onChange) => {
            if (value && typeof value === "object" && Object.hasOwn(value, "id")) {
              setValue(`${id}.dataType` as Path<ICustomFieldSearchForm>, customFieldsDict[value.id].dataType);
              setValue(`${id}.customFieldId` as Path<ICustomFieldSearchForm>, value.id);
              setValue(`${id}.customField` as Path<ICustomFieldSearchForm>, {
                ...customFieldsDict[value.id],
                id: customFieldsDict[value.id].id,
                name: customFieldsDict[value.id].name,
                dataType: customFieldsDict[value.id].dataType,
              });
              setValue(`${id}.value` as Path<ICustomFieldSearchForm>, null);
              if (
                !CustomFieldDataTypeUtils.IsValidOperatorForCustomFieldDataType(
                  filter.operator,
                  customFieldsDict[value.id].dataType
                )
              ) {
                setValue(`${id}.operator` as Path<ICustomFieldSearchForm>, "EQUALS");
              }
            } else {
              setValue(`${id}.dataType` as Path<ICustomFieldSearchForm>, null);
              setValue(`${id}.operator` as Path<ICustomFieldSearchForm>, "EQUALS");
              setValue(`${id}.customFieldId` as Path<ICustomFieldSearchForm>, null);
              onChange(value);
            }
          }}
        />
      </div>
      <div>
        <GenericVirtualizedSelectFormField
          id={`${id}.operator` as any}
          label={"Operator"}
          items={
            filter?.customField?.id
              ? Array.from(
                  CustomFieldDataTypeUtils.MapCustomFieldValueOperatorToCustomFieldDataType(
                    customFieldsDict?.[filter.customField.id].dataType
                  )
                )
              : []
          }
          control={control}
          placeholder={"Select operator"}
          required={true}
          isMulti={false}
          allowCreateEntity={false}
          horizontal={false}
          allowX={false}
          onRowStartRenderer={onRowStartRenderer}
        />
      </div>
      <div>
        {filter.customField && (
          <CustomFieldFilterFormRenderer
            control={control}
            errors={errors}
            id={`${id}.value` as any}
            operator={filter?.operator}
            customField={filter.customField}
          />
        )}
      </div>
    </div>
  );
};

// Main wrapper
interface CustomFieldFilterBuilderProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  defaultValues?: ICustomFieldSearchForm;
  onCreate: (data: ICustomFieldSearchForm) => void;
  customFieldsDict: Record<string, CustomField>;
  entityTypes: CustomTypeFilters["entityTypes"];
}

export const CustomFieldFilterBuilder = ({
  show,
  setShow,
  defaultValues,
  onCreate,
  customFieldsDict,
  entityTypes,
}: CustomFieldFilterBuilderProps) => {
  // This function is used to get the default values for the form.
  const getDefaultValues = useCallback(
    (values: ICustomFieldSearchForm | undefined, customFieldsDict: MultiEditMappedItemObject<CustomField>) => {
      if (!values)
        return {
          id: uuidv4(),
          name: "",
          type: "flat",
          active: true,
          showInSidebar: true,
          filterQuery: {},
        } as ICustomFieldSearchForm;
      return {
        ...values,
        id: values.id ?? "",
        name: values.name ?? "",
        filterQuery: convertICustomFieldSearch(values.filterQuery, customFieldsDict),
      } as ICustomFieldSearchForm;
    },
    []
  );

  const renderFn = useCallback(
    (customFieldsDict: MultiEditMappedItemObject<CustomField>) => {
      // const _defaultValues = defaultValues ? getDefaultValues(defaultValues, customFieldsDict) : defaults;
      const _defaultValues = getDefaultValues(defaultValues, customFieldsDict);
      const initialFilterOption = defaultValues?.filterQuery
        ? countNodesInCustomFieldSearchQuery(defaultValues.filterQuery) > 1
          ? "tree"
          : "flat"
        : undefined;
      return (
        <CustomFieldFilterBuilderCore
          show={show}
          setShow={setShow}
          onCreate={onCreate}
          defaultValues={_defaultValues}
          initialFilterOption={initialFilterOption}
          customFieldsDict={customFieldsDict}
          entityTypes={entityTypes}
        />
      );
    },
    [defaultValues, entityTypes, getDefaultValues, onCreate, setShow, show]
  );

  return <>{customFieldsDict && show && renderFn(customFieldsDict)}</>;
};

export interface ICustomFieldSearchForm {
  id: string;
  name: string | null;
  type: CustomFieldFilterType;
  active: boolean;
  showInSidebar: boolean;
  filterQuery: CustomFieldSearchQueryForm | null;
}

interface CustomFieldFilterBuilderCoreProps extends CustomFieldFilterBuilderProps {
  defaultValues: ICustomFieldSearchForm;
  initialFilterOption?: CustomFieldFilterType;
  customFieldsDict: MultiEditMappedItemObject<CustomField>;
}

export const CustomFieldFilterBuilderCore = ({
  show,
  setShow,
  onCreate,
  defaultValues,
  initialFilterOption,
  customFieldsDict,
  entityTypes,
}: CustomFieldFilterBuilderCoreProps) => {
  const [filterOption, setFilterOption] = useState<CustomFieldFilterType | undefined>(initialFilterOption || "flat");

  const {
    watch,
    control,
    register,
    reset,
    setValue,
    getValues,
    formState: { errors, dirtyFields },
    handleSubmit,
  } = useForm<ICustomFieldSearchForm>({
    defaultValues: useMemo(() => Object.freeze(defaultValues), [defaultValues]),
    resolver: (data, context, options) => yupCustomFieldFilterValidationResolver(data, context, options, true),
  });

  const filterQuery = watch("filterQuery");
  const name = watch("name");

  useEffect(() => {
    if (!Object.hasOwn(dirtyFields, "name") || !dirtyFields.name) {
      if (filterQuery?.customField) {
        if (filterQuery?.operator) {
          setValue("name", `${filterQuery.customField.name} ${filterQuery.operator.replace("_", " ").toLowerCase()}`);
        }
      }
    }
  }, [filterQuery?.customField, filterQuery?.operator, setValue, dirtyFields, name]);

  return (
    <Modal
      isOpen={show}
      onClose={() => {
        setFilterOption(undefined);
        setShow(false);
      }}
      style={{ width: "100%", maxWidth: "90vw", height: "100%", maxHeight: "90vh" }}
    >
      <Modal.Title>
        {filterOption ? (
          <>
            <InputFormField
              id={"name"}
              label={"Name"}
              errors={errors}
              register={register}
              autoFocus={true}
              required={true}
              placeholder={"Insert name..."}
              style={{ width: "100%" }}
            />
            <input type="text" {...register("id")} readOnly hidden />
            <input type="text" {...register("type")} readOnly hidden />
            <input type="checkbox" {...register("active")} readOnly hidden />
            <input type="checkbox" {...register("showInSidebar")} readOnly hidden />
          </>
        ) : (
          <div className="flex row-nowrap align-center gap-5">
            <h3 style={{ margin: 0 }}>Custom filters</h3>
            <OverlayInfo icon="info">
              <div>
                <p>
                  Create custom filters to search for specific custom field values. You can create simple filters or
                  logic trees.
                </p>
              </div>
            </OverlayInfo>
          </div>
        )}
      </Modal.Title>
      <Modal.Body
        style={{
          overflow: "hidden",
          ...(filterOption && { padding: "5px", alignItems: "center", justifyContent: "center" }),
          // width: "fit-content",
          // height: "fit-content",
          // maxWidth: "90vw",
          // maxHeight: "90vh",
        }}
      >
        <div className="flex col-nowrap" style={{ width: "100%", height: "100%", overflow: "hidden" }}>
          {filterOption === "tree" ? (
            <div
              className="flex"
              style={{ width: "100%", height: "fit-content", padding: "10px 10px 10px 0px", overflow: "auto" }}
            >
              <RenderTreeStructurStateProvider>
                {({ height, setHeight }) => (
                  <RenderFilterTreeStructure
                    id={"filterQuery"}
                    control={control}
                    register={register}
                    errors={errors}
                    reset={reset}
                    setValue={setValue}
                    getValues={getValues}
                    height={height}
                    setHeight={setHeight}
                    customFieldsDict={customFieldsDict}
                    entityTypes={entityTypes}
                  />
                )}
              </RenderTreeStructurStateProvider>
            </div>
          ) : (
            <div
              className="flex"
              style={{ width: "100%", height: "fit-content", padding: "10px 10px 10px 0px", overflow: "auto" }}
            >
              <RenderSimpleFilterStructure
                id={"filterQuery"}
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                customFieldsDict={customFieldsDict}
                entityTypes={entityTypes}
              />
            </div>
          )}
          {!!Object.keys(errors).length && (
            <div className="flex" style={{ width: "100%", height: "fit-content", padding: "5px", overflow: "auto" }}>
              <FormErrors errors={errors} />
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Controls>
        {filterOption && (
          <div className="flex row-nowrap align-center">
            {filterOption === "flat" && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  const values = getValues();
                  const vals = {
                    id: values.id || uuidv4(),
                    name: values.name || "",
                    type: "tree",
                    active: true,
                    showInSidebar: true,
                    filterQuery: { left: values.filterQuery ?? null, right: null, predicate: "AND" },
                  } as ICustomFieldSearchForm;
                  reset(vals);
                  setFilterOption("tree");
                }}
                title="Advanced filter"
              >
                <LucideIcon name="network" /> Advanced filter
              </button>
            )}
            <div className="flex gap-5" style={{ marginLeft: "auto" }}>
              <button
                className="btn btn-ghost-secondary"
                onClick={() => {
                  setFilterOption(undefined);
                  setShow(false);
                }}
                title="Cancel"
              >
                <LucideIcon name="x" /> Cancel
              </button>
              <button
                className="btn btn-ghost-secondary"
                onClick={() => {
                  setFilterOption(defaultValues.type);
                  reset(defaultValues);
                }}
                title="Revert"
              >
                <LucideIcon name="refresh-ccw" /> Revert
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit(async (data) => {
                  const _data = {
                    ...data,
                    type:
                      countNodesInCustomFieldSearchQuery(data.filterQuery) > 1
                        ? ("tree" as CustomFieldFilterType)
                        : ("flat" as CustomFieldFilterType),
                    filterQuery: convertCustomFieldSearchQueryForm(data.filterQuery),
                  };
                  onCreate(_data);
                  setFilterOption(undefined);
                  setShow(false);
                })}
                title="Save changes"
              >
                Save Changes
              </button>
            </div>
          </div>
        )}
      </Modal.Controls>
    </Modal>
  );
};
